import React from 'react';
import { Grid, TextField, Checkbox, FormControlLabel, Box, Typography, Stack, Alert } from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { sa, PillButton } from '../../../components/superAdminUi';
import SectionCard from '../../../components/common/SectionCard';
import GroupFormFields from '../../../components/common/GroupFormFields';

// The edit modal kept its own hardcoded customer-type list; carry it over unchanged.
const CUSTOMER_TYPE_OPTIONS = [
  'English', 'Welsh', 'Scottish', 'CofE', 'Catholic', 'Other Faith', 'International',
  'GCC', 'UAE', 'Saudi', 'Qatar', 'Oman', 'Special School',
];

// The edit modal's values use camelCase keys; map the shared body onto them.
const EDIT_FIELD_NAMES = {
  groupType: 'groupType',
  matGroupUID: 'matGroupUID',
  country: 'country',
  dataStorageLocation: 'dataStorageLocation',
  customerType: 'customerType',
};

// Step 1 of the group-edit wizard: the same fields EditGroupModal edited — name + display
// name, the shared classification body, settings toggles, and the separate Edit
// Subscription hand-off (subscription is not edited inline) — laid out as bordered cards
// so the edit surface reads the same as the create wizard.
export default function GroupDetailsStep({
  formik,
  storageLocations,
  duplicate,
  serverErrors,
  onDismissError,
  onGroupFieldEdit,
  onEditSubscription,
}) {
  const { values, handleChange, touched, errors, setFieldValue } = formik;

  return (
    <Stack spacing={2}>
      {duplicate && duplicate.isDuplicate && (
        <Alert severity="warning">
          A group with this name already exists
          {duplicate.existingName ? `: ${duplicate.existingName}` : ''}.
        </Alert>
      )}

      {serverErrors.map((message, index) => (
        <Alert severity="error" key={index} onClose={() => onDismissError(index)}>{message}</Alert>
      ))}

      <SectionCard title="Group details" icon={<GroupsOutlinedIcon fontSize="small" />}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Group Name"
              name="name"
              value={values.name}
              onChange={(e) => { handleChange(e); if (onGroupFieldEdit) onGroupFieldEdit(); }}
              error={Boolean(touched.name && errors.name)}
              helperText={touched.name && errors.name}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Display Name (Group Name)"
              name="groupName"
              value={values.groupName}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <GroupFormFields
        values={values}
        onFieldChange={setFieldValue}
        names={EDIT_FIELD_NAMES}
        customerTypeOptions={CUSTOMER_TYPE_OPTIONS}
        storageLocations={storageLocations}
      />

      <SectionCard title="Settings" icon={<TuneOutlinedIcon fontSize="small" />}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Checkbox checked={values.isDisplayMatHeatMap} onChange={(e) => setFieldValue('isDisplayMatHeatMap', e.target.checked)} />}
              label="Display MAT Heat Map"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Checkbox checked={values.isAnnonymised} onChange={(e) => setFieldValue('isAnnonymised', e.target.checked)} />}
              label="Anonymized"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Checkbox checked={values.isTestAccount} onChange={(e) => setFieldValue('isTestAccount', e.target.checked)} />}
              label="Test Account"
            />
          </Grid>
        </Grid>
      </SectionCard>

      <Box sx={{ p: 2, bgcolor: sa.color.page, border: `1px solid ${sa.color.stroke}`, borderRadius: sa.radius.lg }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Box>
            <Typography variant="subtitle1" gutterBottom><strong>Subscription Management</strong></Typography>
            <Typography variant="body2" color="text.secondary">
              Edit subscription details, products, pricing, and manage billing for this group
            </Typography>
          </Box>
          <PillButton type="button" $variant="danger" $size="lg" onClick={onEditSubscription}>Edit Subscription</PillButton>
        </Stack>
      </Box>
    </Stack>
  );
}
